export const truncatedMessage = (message: string, maxLength = 10) => {
  return message.length > maxLength
    ? message.slice(0, maxLength) + "..."
    : message;
};

export const formatMoney = (value: any) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

export const getPage = (value: any) => {
  return {
    total: value.total,
    size: value.size,
    current: value.current,
    pages: value.pages,
  };
};

export const attachPageinfo = (params: any, pageinfo: any) => {
  // params.hasOwnProperty("")

  params.pageNo = pageinfo.current;
  params.pageSize = pageinfo.size;
  return params;
};

const ALTERNATORS_CATEGORY_ID = "1399533178454016";
const ALTERNATORS_CHINA_CATEGORY_ID = "1399533193142272";

// Legacy product URLs use leaf-first IDs from getProductCatPath to prove ancestry.
export const getLegacyChinaCategoryTarget = (
  categoryId: unknown,
  categoryPathIds?: unknown,
): { path: string; catId?: string } | null => {
  const id = String(categoryId ?? "");
  const path = `/prod/cat/c/${ALTERNATORS_CHINA_CATEGORY_ID}`;

  if (id === ALTERNATORS_CHINA_CATEGORY_ID) {
    return { path };
  }

  if (
    !Array.isArray(categoryPathIds) || categoryPathIds.length < 3 ||
    !categoryPathIds.every((value) =>
      (typeof value === "string" && value !== "") ||
      (typeof value === "number" && Number.isFinite(value)),
    )
  ) {
    return null;
  }

  const ids = categoryPathIds.map(String);
  if (
    ids[0] !== id ||
    ids.at(-2) !== ALTERNATORS_CHINA_CATEGORY_ID ||
    ids.at(-1) !== ALTERNATORS_CATEGORY_ID
  ) {
    return null;
  }

  return { path, catId: id };
};

// 获取顶部导航模板，
// nav_cat_id：当前分类的父类id
//  nav_cur_cat_id: 当前分类id，其值在alternators_prod中时，直接显示分类下的产品
export const getNavTemplateUrl = (nav_cat_id: any, nav_cur_cat_id: any = -1) => {
  // China uses the standard unit template for both API IDs and string route IDs.
  if (
    String(nav_cat_id) === ALTERNATORS_CATEGORY_ID &&
    String(nav_cur_cat_id) === ALTERNATORS_CHINA_CATEGORY_ID
  ) {
    return "/prod/cat/c/";
  }

  //todo: hard code
  // East Europe    korea
  const alternators_prod = [
    1421400377122817, 1421400017403905,
  ];
  // unknow
  const starters_alternators = [1404294612869120, 1399533178454016];
  // unknow
  const sa_parts = [100687873, 1425716939087872];
  // unknow
  const ac_parts = [1399606304530432];

  // Alternator下，显示产品的分类
  if (String(nav_cur_cat_id) !== "-1" && alternators_prod.some((id) => String(id) === String(nav_cur_cat_id))) {
    return "/prod/cat/p/";
  }

  // c显示分类代分组
  if (starters_alternators.some((id) => String(id) === String(nav_cat_id))) {
    return "/prod/cat/c/";
  }

  // c2显示末级分类下分组
  if (sa_parts.some((id) => String(id) === String(nav_cat_id))) {
    return "/prod/cat/c2/";
  }

  // c3显示app
  if (ac_parts.some((id) => String(id) === String(nav_cat_id))) {
    return "/prod/cat/c3/";
  }

  // 直接显示产品
  return "/prod/cat/p/";
};

/**
 * 节流函数
 * @param fn 要执行的函数
 * @param delay 节流延迟时间(毫秒)
 * @returns 包装后的节流函数
 */
export const throttle = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return function (this: any, ...args: Parameters<T>) {
    if (timer !== null) return;

    timer = setTimeout(() => {
      fn.apply(this, args);
      timer = null;
    }, delay);
  };
};

/**
 * 防抖函数
 * @param fn 要执行的函数
 * @param delay 防抖延迟时间(毫秒)
 * @returns 包装后的防抖函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return function (this: any, ...args: Parameters<T>) {
    if (timer !== null) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      fn.apply(this, args);
      timer = null;
    }, delay);
  };
};

/**
 * 深拷贝
 * @param object copy的对象
 * @param cache 临时存储对象
 * @returns target copy后的对象
 */
export const deepClone = (object: any, cache = new WeakMap()) => {
  // 如果不是对象或者是 null，直接返回
  if (typeof object !== "object" || object === null) {
    return object;
  }

  // 如果已经克隆过该对象，直接返回缓存的结果
  if (cache.has(object)) {
    return cache.get(object);
  }

  // 处理特殊对象
  if (object instanceof Date) {
    return new Date(object);
  }

  if (object instanceof RegExp) {
    return new RegExp(object);
  }

  if (object instanceof Map) {
    const clonedMap = new Map();
    cache.set(object, clonedMap);
    object.forEach((value, key) => {
      clonedMap.set(key, deepClone(value, cache));
    });
    return clonedMap;
  }

  if (object instanceof Set) {
    const clonedSet = new Set();
    cache.set(object, clonedSet);
    object.forEach((value) => {
      clonedSet.add(deepClone(value, cache));
    });
    return clonedSet;
  }

  // 处理普通对象和数组
  const target: any = Array.isArray(object) ? [] : {};
  cache.set(object, target);

  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      target[key] = deepClone(object[key], cache);
    }
  }

  return target;
};

/**
 * 构建带查询参数的URL
 * @param baseUrl 基础URL（不包含查询参数）
 * @param queryParams 查询参数对象
 * @returns 完整的URL字符串
 */
export const getApiUrl = (
  baseUrl: string,
  queryParams: Record<string, string | number | boolean | null | undefined>
): string => {
  const params = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, val]) => {
    // 过滤掉 null/undefined，并将非字符串值转为字符串
    if (val != null) {
      params.append(key, String(val));
    }
  });

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * 非编码解析params
 * @param baseUrl 基础路由
 * @param queryParams
 * @returns 完整url
 */
export const getApiUrlWithoutEncoding = (
  baseUrl: string,
  queryParams: Record<string, string | number | boolean | null | undefined>
): string => {
  const pairs: string[] = [];

  Object.entries(queryParams).forEach(([key, val]) => {
    if (val != null && val !== "") {
      pairs.push(`${key}=${val}`); // 不进行URL编码
    }
  });

  const queryString = pairs.join("&");
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * 格式化货币数值：超过阈值时替换为 k/M 单位
 * @param {string|number} value - 输入值（如 "$192,000.00" 或 192000）
 * @param {object} [options] - 配置选项
 * @param {number} [options.threshold=1000] - 启用缩写的阈值
 * @param {number} [options.decimal=1] - 保留的小数位数
 * @param {boolean} [options.keepSymbol=true] - 是否保留货币符号
 * @param {boolean} [options.forceDecimal=false] - 强制显示小数位
 * @returns {string} 格式化后的字符串（如 "$192k"）
 */
export const formatCurrencyWithUnit = (
  value: String,
  options: {
    threshold?: number;
    decimal?: number;
    keepSymbol?: boolean;
    forceDecimal?: boolean;
  } = {}
): string => {
  const {
    threshold = 1000,
    decimal = 2,
    keepSymbol = true,
    forceDecimal = true,
  } = options;

  // 提取货币符号和数字部分
  let symbol = "";
  let numStr = String(value).trim();

  // 匹配货币符号（支持 $€£¥等）
  const symbolMatch = numStr.match(/^[^\d\-+.,]+/);
  if (symbolMatch) {
    symbol = symbolMatch[0];
    numStr = numStr.slice(symbol.length);
  }

  // 移除千位分隔符并转换为数字
  const num = parseFloat(numStr.replace(/,/g, ""));
  if (isNaN(num)) {
    return String(value); // 无效数字返回原值
  }

  // 原始值未超过阈值
  if (Math.abs(num) < threshold) {
    return keepSymbol ? `${symbol}${numStr}` : numStr;
  }

  // 计算单位
  const units = ["k", "M", "B"];
  const unitIndex = Math.min(
    Math.floor(Math.log10(Math.abs(num)) / 3),
    units.length
  );
  const divisor = Math.pow(1000, unitIndex);
  const unit = units[unitIndex - 1] || "k";

  // 格式化数值
  const scaledNum = num / divisor;
  const decimalPlaces = forceDecimal
    ? decimal
    : Math.max(0, decimal - (Math.floor(scaledNum) === scaledNum ? 1 : 0));

  // 重新添加货币符号
  const formattedNum = scaledNum.toFixed(decimalPlaces) + unit;
  return keepSymbol ? `${symbol}${formattedNum}` : formattedNum;
};
